<script>
export default {
    page() {
        const title = 'Account - Edit Profile'
        const description = 'Account - Edit Profile'
        return {
            title,
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },

                { property: 'twitter:title', vmid: 'twitter:title', content: title },
                { property: 'twitter:description', vmid: 'twitter:description', content: description },
            ],
        }
    },
}
</script>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import DataLayer from '@services/data-layer/index'
import Birthday from '@data/models/Birthday'
import months from '@enums/months'
import { email, required, useFormValidation } from '@composables/use-form-validation'
import { useStore } from '@composables/use-store'
import { useMessages } from '@composables/use-messages'

const getMessageText = useMessages()
const router = useRouter()
const store = useStore()
const user = ref(null)
const birthday = ref(new Birthday())
const hasChanges = ref(false)
const isLoaded = ref(false)
const currentYear = new Date().getFullYear()
const startYear = 1900
const crumbs = [
    { label: 'Account Management', to: '/account' },
    { label: 'Edit profile' },
]
const years = computed(() =>
    Array.from({ length: currentYear - startYear + 1 }, (_value, index) => startYear + index)
)
const validation = useFormValidation({
    email: {
        value: computed(() => user.value?.email || ''),
        validators: [required, email],
    },
})

function notify(messageKey, type) {
    store.dispatch('appearance/handleNotification', {
        message: getMessageText(messageKey),
        type,
    })
}

function loadInitUser() {
    user.value = { ...store.state.member.currentMember }
    birthday.value = new Birthday()

    if (user.value.birthday) {
        const date = new Date(user.value.birthday)
        birthday.value.day = date.getDate()
        birthday.value.month = date.getMonth()
        birthday.value.year = date.getFullYear()
    }
}

function setDate() {
    if (birthday.value.day && birthday.value.month && birthday.value.year) {
        const timezoneOffset = new Date().getTimezoneOffset()
        user.value.birthday = new Date(
            birthday.value.year,
            birthday.value.month,
            birthday.value.day,
            0,
            -timezoneOffset
        ).toJSON()
    }
}

function save() {
    if (validation.$invalid) {
        notify('EMAIL_REQUIRED', 'warning')
        validation.$reset()
        return undefined
    }

    return DataLayer.members
        .edit({ id: user.value.id, data: user.value })
        .then(response => {
            hasChanges.value = false
            store.commit('member/SET_CURRENT_MEMBER', response.body)
            notify('DETAILS_UPDATED', 'success')
            router.push('/account')
        })
        .catch(error => {
            if (error.response.status === 409) {
                notify('EMAIL_ALREADY_EXISTS', 'danger')
                return
            }
            notify('SOMETHING_WENT_WRONG', 'danger')
        })
}

function goBack() {
    router.push('/account')
}

watch(
    () => store.state.member.currentMember,
    loadInitUser
)
watch(
    user,
    () => {
        hasChanges.value = isLoaded.value
        isLoaded.value = true
    },
    { deep: true }
)
onMounted(loadInitUser)
</script>

<template>
    <div>
        <BaseBreadcrumbs :crumbs="crumbs"/>
        <h1 class="title">Edit Profile</h1>
        <form
            v-if="user"
            @submit.prevent="save"
        >
            <div class="form-container">
                <div class="field-container">
                    <label class="label">Email</label>
                    <BaseInput
                        v-model.lazy="user.email"
                        :class="['field', validation.email.$error && 'error']"
                        placeholder="Enter email"
                    />
                </div>
                <div class="field-container">
                    <label class="label">First name</label>
                    <BaseInput
                        v-model.lazy="user.firstName"
                        class="field"
                        placeholder="Enter first name"
                    />
                </div>
                <div class="field-container">
                    <label class="label">Last name</label>
                    <BaseInput
                        v-model.lazy="user.lastName"
                        class="field"
                        placeholder="Enter last name"
                    />
                </div>
                <div class="field-container">
                    <label class="label">Date of Birth</label>
                    <div class="birthday-fields">
                        <BaseSelect
                            v-model="birthday.day"
                            @change="setDate"
                        >
                            <option
                                class="option"
                                :value="null"
                                disabled
                                selected
                            >Day</option>
                            <option
                                v-for="d in 31"
                                :key="d + 'day'"
                                :value="d"
                                class="option"
                            >
                                {{ d }}
                            </option>
                        </BaseSelect>
                        <BaseSelect
                            v-model="birthday.month"
                            @change="setDate"
                        >
                            <option
                                class="option"
                                :value="null"
                                disabled
                                selected
                            >Month</option>
                            <option
                                v-for="month in months"
                                :key="month.title + month.value"
                                :value="month.value"
                                class="option"
                            >
                                {{ month.title }}
                            </option>
                        </BaseSelect>
                        <BaseSelect
                            v-model="birthday.year"
                            @change="setDate"
                        >
                            <option
                                class="option"
                                :value="null"
                                disabled
                                selected
                            >Year</option>
                            <option
                                v-for="y in years"
                                :key="y + 'day'"
                                :value="y"
                                class="option"
                            >
                                {{ y }}
                            </option>
                        </BaseSelect>
                    </div>
                </div>
            </div>
            <div class="actions">
                <BaseButton
                    button-type="transparent"
                    size="medium"
                    type="button"
                    @click="goBack"
                >
                    <span>Cancel</span>
                </BaseButton>
                <BaseButton
                    class="save-button"
                    :disabled="!hasChanges"
                    button-type="tagBlack"
                    size="medium"
                    type="submit"
                    @click="() => {}"
                >Save profile</BaseButton>
            </div>
        </form>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.title {
    margin-top: 0;
}

.form-container {
    display: flex;
    flex-direction: column;
    gap: $s-l;
}

.field-container {
    display: flex;
    flex-direction: column;
    gap: $s-m;
}

.label {
    @extend %contentSubtitleM;
}

.birthday-fields {
    display: grid;
    grid-template-columns: 2fr 3fr 2fr;
    gap: $s-xs;
}

.actions {
    display: flex;
    gap: $s-xl;
    align-items: center;
    justify-content: center;
    margin-top: $s-xxl;

    .save-button {
        padding: $s-m $s-l;
        font-weight: unset;
        text-transform: unset;
    }
}

@include tablet {
    .birthday-fields {
        grid-template-columns: 1fr 2fr 4fr;
        gap: $s-s;
    }

    .actions {
        justify-content: flex-end;
    }
}
</style>
